import { RegisterOptions, Validate } from 'react-hook-form'
import { TFunction } from 'next-i18next'
import { DateOfBirthFieldSchema } from '@afterpaytouch/form'
import { addressHasNoPO, AllCountryCode, InputAddressData, STATES } from '@afterpaytouch/core'
import { isValidEmail } from '../../utils/validation'
import { gbAddressGapError } from '../../utils/profile-validation'
import { getStateFullFromFormData } from './helper'
import { MAX_AGE_RESTRICTION } from './constants'
import { validAddressOption } from '../CardForm/validations'
import { PaymentType } from '@afterpay/types'

export type BlackListedStatesRecord = Partial<Record<AllCountryCode, string[]>>

interface BlacklistedStates {
  [PaymentType.PCL]: BlackListedStatesRecord
  [PaymentType.PBI]: BlackListedStatesRecord
}

export const BLACKLISTED_STATES: BlacklistedStates = {
  [PaymentType.PCL]: {
    US: [STATES.US.label.WV, STATES.US.label.NV, STATES.US.label.HI, STATES.US.label.NM],
  },
  [PaymentType.PBI]: {
    US: [STATES.US.label.NM],
  },
}

export const namePattern =
  (t: TFunction) =>
  (name: string): RegisterOptions => {
    const validationRegex = /^$|^[a-zA-Z\u00C0-\u017F][a-zA-Z\u00C0-\u017F- ']*$/
    return {
      pattern: {
        value: validationRegex,
        message: t('signUp:error:invalidName'),
      },
    }
  }

const MINIMUM_LENGTH_VALIDATION: { [x: string]: number } = {
  NZ: 2,
}

export const validName = (name: string): boolean => {
  const INVALID_NAMES = ['registered', 'guest', 'user']
  const isValidName = !INVALID_NAMES.includes(name.toLowerCase())
  return isValidName
}

export const nameMinimumLengthCheck = (name: string, country: AllCountryCode): boolean => {
  const minimumLengthCheck = MINIMUM_LENGTH_VALIDATION[country ?? 'AU'] ?? 0
  const isValidLength = name.length >= minimumLengthCheck
  return isValidLength
}

export const nameValidation = (
  t: TFunction
): {
  validate: {
    validUserName: (v: string) => boolean | string
  }
} => {
  return {
    validate: {
      validUserName: (v) => {
        const isValidName = validName(v)
        return isValidName || (t('signUp:error:unacceptedName') as unknown as string)
      },
    },
  }
}

export const nameValidationWithLength = ({
  name,
  country,
  t,
}): {
  validate: {
    validUserName: (v: string) => boolean | string
    validNameLength: (v: string, country: AllCountryCode) => boolean | string
  }
} => {
  return {
    validate: {
      validUserName: (v) => {
        const isValidName = validName(v)
        return isValidName || t('signUp:error:unacceptedName')
      },
      validNameLength: (v) => {
        const isValidLength = nameMinimumLengthCheck(v, country)
        return isValidLength || t('signUp:error:invalidNameLength', { context: name })
      },
    },
  }
}

export const emailValidation = (t: TFunction) => (name: string) => {
  return {
    validate: {
      validEmail: (v) => {
        const isValidEmailInput = isValidEmail(v)
        return isValidEmailInput || t('signUp:error:email:invalidEmail')
      },
    },
  }
}

export const requiredValidation =
  (t: TFunction) =>
  (name: string): RegisterOptions => ({
    required: t('signUp:error:required', { context: name }) as unknown as string,
  })

export const dobValidation =
  (t: TFunction, minAge: number) =>
  (name: string): Omit<DateOfBirthFieldSchema, 'type'> => {
    return {
      minAge: {
        value: minAge,
        message: t('signUp:error:underAge', { context: name, minAge }),
      },
      maxAge: {
        value: MAX_AGE_RESTRICTION,
        message: t('signUp:error:overAge', { context: name, maxAge: MAX_AGE_RESTRICTION }),
      },
    }
  }

export const requiredAddress = ({ name, country, t }): Record<string, Validate<InputAddressData, any>> => {
  return {
    requiredAddressFields: (v, _) => {
      if (v !== null && typeof v === 'object') {
        const gbGapError = gbAddressGapError(v, country, t)
        if (gbGapError !== null) {
          return gbGapError
        }
        if (validAddressOption(v, country)) {
          return true
        }
      }
      return t('signUp:error:blankAddress', { context: name })
    },
  }
}
interface IsStateEligibleForPayByInstallmentArgs {
  countryCode: string
  state?: string
}

export const isStateEligibleForPayByInstallment = (address: IsStateEligibleForPayByInstallmentArgs): boolean => {
  const ineligibleStates: string[] = BLACKLISTED_STATES.PBI[address?.countryCode]
  return !ineligibleStates?.includes(address?.state ?? '')
}

export const validateState = (isStateEligibleForPayByInstallmentEnabled: boolean, t: TFunction): Record<string, Validate<InputAddressData, any>> => {
  return {
    validState: (v, _) => {
      const stateFull = getStateFullFromFormData({ countryCode: v.countryCode, state: v.state }, t('signUp:error.blacklistedStateDefaultState'))
      const hasNoPayByInstallmentError = isStateEligibleForPayByInstallmentEnabled
        ? isStateEligibleForPayByInstallment({ countryCode: v.countryCode, state: v.state })
        : true
      const blacklistedStateErrorMsg = t('signUp:error.blacklistedState', { stateFull })
      return hasNoPayByInstallmentError || blacklistedStateErrorMsg
    },
  }
}
export const validatePoBox = (message: string): Record<string, Validate<InputAddressData, any>> => {
  return {
    noPoBox: (address, _) => {
      return addressHasNoPO(address) || message
    },
  }
}
